import * as React from 'react';
import * as _ from 'lodash';
import history from 'login-utils/history';
import { DataShape, Field, FormProps } from 'redux-form';
import { css, StyleSheet } from 'aphrodite';
import * as queryString from 'query-string';

import { a11y, ActionButton, colors, Heading, media, mixins, TitleCard } from '@blend/consumer-components';
import { diagnostics, inputFormatters, utils } from '@blend/ui-common';

import * as journeyChooserAPI from 'login-apis/journeyChooserAPI';

import { paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';
import { LoanTypeButtonConfig, loanTypeChooserConfigs } from 'login-components/ChooseLoanType/ChooseLoanType';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import ConstructionSVG from 'login-components/Common/svg/ConstructionSVG';
import FoundSVG from 'login-components/Common/svg/FoundSVG';
import InputMap from 'login-components/Common/Inputs/InputMap';
import LoadingSpinner from 'login-components/Common/LoadingSpinner/LoadingSpinner';
import LookingSVG from 'login-components/Common/svg/LookingSVG';
import BorderlessIconButton, { BorderlessIconButtonConfig } from 'login-components/Common/Buttons/BorderlessIconButton';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';
import RefinanceSVG from 'login-components/Common/svg/RefinanceSVG';
import { AcceptedTermsTypes, softCreditConsentTypes } from 'login-components/Common/Inputs/RequirementInput/utils';
import RequiredViewingRequirementInput from 'login-components/Common/Inputs/RequirementInput/RequiredViewingRequirementInputContainer';
import ThinkingSVG from 'login-components/Common/svg/ThinkingSVG';
import TransitioningFields from 'login-components/Common/TransitioningFields/TransitioningFields';
import States from 'login-constants/states';

import * as deploymentConfig from 'login-utils/deploymentConfig';
import * as onboardingForm from 'login-utils/onboardingForm';
import * as globalWrappers from 'login-utils/globalWrappers';
import * as loanUtils from 'login-utils/loanUtils';
import LargeIconButton from 'login-components/Common/Buttons/LargeIconButton';

const { domUtils, featureFlags } = utils;

const supportedTimelineConfigs: BorderlessIconButtonConfig[] = [
  {
    value: 'NOT_STARTED',
    IconComponent: ThinkingSVG,
    buttonId: 'it-journeyChooserNotStarted',
    text: "I'm just thinking about buying",
  },
  {
    value: 'NOT_IN_CONTRACT',
    IconComponent: LookingSVG,
    buttonId: 'it-journeyChooserNotInContract',
    text: "I'm making offers",
  },
  {
    value: 'FOUND',
    IconComponent: FoundSVG,
    buttonId: 'it-journeyChooserFound',
    text: 'I signed a purchase contract',
  },
  {
    value: 'REFINANCE',
    IconComponent: RefinanceSVG,
    buttonId: 'it-journeyChooserRefinance',
    text: 'I want to refinance my home',
  },
  {
    value: 'CONSTRUCTION',
    IconComponent: ConstructionSVG,
    buttonId: 'it-journeyChooserConstruction',
    text: 'I am building a new home or making renovations to an existing home',
  },
  {
    value: 'CONSTRUCTION_PERM',
    IconComponent: ConstructionSVG,
    buttonId: 'it-journeyChooserConstructionPerm',
    text: 'I am building a new home',
  },
];

// A subset of supportedTimelineConfigs that is the set of options we present to the borrower to choose from.
// In theory, a borrower can select any of the configs in supportedTimelineConfigs, but we limit the list of freely
// choosable options to streamline the user experience.
const selectableConfigs = _.pickBy(supportedTimelineConfigs, ({ value }: BorderlessIconButtonConfig) =>
  _.includes(['NOT_STARTED', 'NOT_IN_CONTRACT', 'FOUND', 'REFINANCE'], value),
) as BorderlessIconButtonConfig[];

export function getTimelineChooserConfig(value: onboardingForm.TimelineValue) {
  return _.find(supportedTimelineConfigs, { value });
}

export const baseInputs: FormInput[] = [
  {
    ...InputMap.RequirementInput,
    identifier: 'terms & conditions',
    name: 'acceptTermsAndConditions',
    label: 'Terms & Conditions',
  },
  {
    ...InputMap.UnformattedRequirementInput,
    identifier: 'consent to be contacted',
    name: 'giveConsentToLenderContact',
  },
];

function filterRequiredConsentsForRequirementInput(
  requiredConsents: AcceptedTermsTypes[],
  softCreditConsentSupportedValues: onboardingForm.TimelineValue[],
  selectedTimelineValue: onboardingForm.TimelineValue | null,
): AcceptedTermsTypes[] {
  return _.filter(requiredConsents, consent => {
    // If we're using upfront credit pull configs, we don't care about respecting softCreditConsentSupportedValues,
    // which is a one-tap-specific concept.
    // Once the flag is removed, we should remove all references to softCreditConsentSupportedValues.
    if (
      _.includes(softCreditConsentTypes, consent) &&
      !featureFlags.isEnabled('useUpfrontCreditPullConfigsInJC_20200305')
    ) {
      return _.includes(softCreditConsentSupportedValues, selectedTimelineValue);
    }
    return consent !== 'consentToLenderContact';
  });
}

export function generateInputs(
  requiredConsents: AcceptedTermsTypes[],
  softCreditConsentSupportedValues: onboardingForm.TimelineValue[],
  selectedTimelineValue: onboardingForm.TimelineValue | null,
  phoneNumber: JourneyChooser.Props['phoneNumber'],
): FormInput[] {
  return _.compact([
    {
      ...(_.find(baseInputs, { name: 'acceptTermsAndConditions' }) as FormInput),
      acceptedTermsTypes: filterRequiredConsentsForRequirementInput(
        requiredConsents,
        softCreditConsentSupportedValues,
        selectedTimelineValue,
      ),
    },
    _.includes(requiredConsents, 'consentToLenderContact') && {
      ...(_.find(baseInputs, { name: 'giveConsentToLenderContact' }) as FormInput),
      displayParts: [
        'I ',
        {
          type: 'openModal',
          display: 'consent to be contacted',
          textScreenId: 'consent-to-lender-contact',
          headerText: 'Consent to Contact',
        },
        ' at ',
        inputFormatters.PHONE.format(phoneNumber),
        '. ',
        {
          display: 'Edit',
          onClick: () => {
            history.push(`${States.PROVIDE_PHONE_NUMBER}`);
          },
        },
      ],
      excludeFullstory: true,
    },
  ]);
}

const styles = StyleSheet.create({
  loanTypeWrapper: {
    marginBottom: 32,
  },
  timelineContainer: {
    display: 'block',
    ...mixins.paddingHorizontal(8),
    [media.smallView]: {
      ...mixins.paddingHorizontal(8),
    },
  },
  cardPadding: {
    ...mixins.padding(24),
  },
  fieldLabel: {
    marginBottom: 16,
  },
  loadingSpinner: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 32,
  },
  editButton: {
    ...mixins.paddingVertical(12),
    textAlign: 'center',
  },
  buttonWrapper: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  },
  inputBorderBottom: {
    ':not(:last-child)': {
      paddingBottom: 16,
      borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    },
  },
  errorWrapper: {
    paddingTop: 16,
  },
  srOnlyContentText: mixins.hideOnMobile(),
});

namespace JourneyChooser {
  export interface StateProps {
    phoneNumber: string;
    loanType: LoanType;
    selectedTimelineValue: onboardingForm.TimelineValue | null;
  }

  export interface DispatchProps {
    setPhoneNumber: (phoneNumber: string) => void;
    selectTimelineValue: (value: StateProps['selectedTimelineValue']) => void;
  }

  export type OwnFormProps = FormProps<DataShape, {}, ReduxState>;

  export type Props = OwnFormProps & StateProps & DispatchProps & ReactRouter.RouteComponentProps<{}, {}>;

  export interface State {
    visibleTimelineChooserOptions: typeof supportedTimelineConfigs;
    isFetchingEntryInfo: boolean;
    submitButtonDisabled: boolean;
    submitError: string | null;
  }
}

class JourneyChooser extends React.Component<JourneyChooser.Props, JourneyChooser.State> {
  useRequiredViewingInput: boolean;
  showTimelineConfigs: boolean;
  showChosenLoanType: boolean;
  loanType: LoanType;
  requiredConsents: AcceptedTermsTypes[];
  softCreditConsentSupportedValues: onboardingForm.TimelineValue[];
  journeyOptionIsLocked: boolean;
  entryInfo: journeyChooserAPI.JourneyChooserEntryInfo = {} as any;

  constructor(props: JourneyChooser.Props) {
    super(props);
    const existingSelectedTimelineValue = props.selectedTimelineValue;
    this.useRequiredViewingInput = featureFlags.isEnabled('requireViewingTermsBeforeAccepting20190904');
    this.state = {
      visibleTimelineChooserOptions: existingSelectedTimelineValue
        ? [getTimelineChooserConfig(existingSelectedTimelineValue)!]
        : selectableConfigs,
      isFetchingEntryInfo: true,
      submitButtonDisabled: false,
      submitError: null,
    };
  }

  async componentDidMount() {
    const { setPhoneNumber, phoneNumber: formPhoneNumber, selectedTimelineValue, location } = this.props;
    try {
      const loanId = loanUtils.getSelectedProductId()!; // the middleware will redirect us if this is undefined
      this.entryInfo = await journeyChooserAPI.getJourneyChooserInfo(loanId);

      // Because the JourneyChooser lives in a multi-page form, users can leave and come back to it while working on
      // the same form. The componentDidMount runs each time the user returns to this page, so values on the form
      // may already be set. We assume that the values written in Redux-Form are the most up-to-date since the user
      // interacted with them most recently; as such, we never use the API results to overwrite values on the form.
      if (!formPhoneNumber && this.entryInfo.phoneNumber) setPhoneNumber(this.entryInfo.phoneNumber);
      if (!selectedTimelineValue) {
        // The journeyOption can be passed in as a query param from the SAML payload
        const { journeyOption: queryJourneyOption } = queryString.parse(location.search);
        // If the journeyOption cannot be edited, we defer to using the one provided by the API, because it's the one
        // that is already written on the loan
        // Otherwise, we defer to using the one passed in the SAML payload, since it is likely the one that the user
        // most recently selected
        if (!this.entryInfo.journeyOptionIsLocked && !!queryJourneyOption) {
          this.onTimelineValueSelect(queryJourneyOption);
        } else {
          this.onTimelineValueSelect(this.entryInfo.journeyOption);
        }
      }
    } catch (err) {
      diagnostics.error(err);
    }
    this.setState(
      {
        isFetchingEntryInfo: false,
      },
      () => {
        domUtils.focusByNameOrId('js-form-content');
      },
    );
  }

  onJourneyChooserSubmit = async values => {
    const { selectedTimelineValue } = this.props;
    this.setState({ submitButtonDisabled: true, submitError: null });
    try {
      await journeyChooserAPI.journeyChooserSubmit(
        values,
        loanUtils.getSelectedProductId()!, // the middleware will redirect us if this is undefined
        filterRequiredConsentsForRequirementInput(
          this.entryInfo.requiredConsents,
          this.entryInfo.softCreditConsentSupportedValues,
          selectedTimelineValue,
        ),
      );
      return globalWrappers.setHref('/');
    } catch (err) {
      diagnostics.error(err);
      this.setState({
        submitButtonDisabled: false,
        submitError:
          'We encountered an issue while processing your information. Please refresh the page and try again.',
      });
    }
  };

  onTimelineValueSelect = value => {
    const { selectTimelineValue } = this.props;
    const selectedConfig = getTimelineChooserConfig(value);
    // We only support a subset of possible configs that could be passed from the loan, so it's possible for this to
    // be undefined. We don't take the value on the loan into account if it's unsupported.
    if (!selectedConfig) return;

    selectTimelineValue(value);
    this.setState({
      visibleTimelineChooserOptions: [selectedConfig],
    });
  };

  onTimelineValueUnselect = () => {
    const { selectTimelineValue } = this.props;
    selectTimelineValue(null);
    domUtils.focusByNameOrId('js-form-content');
    this.setState({
      visibleTimelineChooserOptions: selectableConfigs,
    });
  };

  getTimelineTransitionConfigs = (): TransitioningFields.FieldConfig[] => {
    const { selectedTimelineValue } = this.props;
    const { visibleTimelineChooserOptions } = this.state;
    const timelineTransitionConfigs = _.map(visibleTimelineChooserOptions, option => {
      const onClick = selectedTimelineValue
        ? _.noop
        : () => {
            this.onTimelineValueSelect(option.value);
          };
      return {
        key: option.value,
        style: 'fadeAndSlide',
        component: (
          <div className={css(styles.buttonWrapper)}>
            <BorderlessIconButton {...option} onClick={onClick} type="button" selectable={!selectedTimelineValue} />
          </div>
        ),
      } as TransitioningFields.FieldConfig;
    });

    const editButtonTransitionConfig: TransitioningFields.FieldConfig = {
      key: 'JourneyChooser-timelineEdit',
      in: !!selectedTimelineValue && !this.entryInfo.journeyOptionIsLocked,
      style: 'fadeAndSlide',
      component: (
        <div className={css(styles.editButton)}>
          <ActionButton
            sizing={'xs'}
            option="secondary"
            type="button"
            onClick={this.onTimelineValueUnselect}
            color={deploymentConfig.getOrganizationColors().action}
            data-it="JourneyChooser-timelineEdit"
          >
            Edit
          </ActionButton>
        </div>
      ),
    };

    return [...timelineTransitionConfigs, editButtonTransitionConfig];
  };

  renderTermsAndConditions = onSubmit => {
    const { phoneNumber, selectedTimelineValue } = this.props;
    const { submitButtonDisabled, submitError } = this.state;

    if (this.useRequiredViewingInput) {
      return (
        <React.Fragment>
          <div className={css(styles.fieldLabel)}>
            <label>
              <Heading
                text={'Terms & Conditions'.toUpperCase()}
                rank={2}
                fontSize={12}
                font="Roboto"
                weight={600}
                color={colors.clayPlus3}
              />
            </label>
          </div>
          <RequiredViewingRequirementInput
            acceptedTermsTypes={filterRequiredConsentsForRequirementInput(
              this.entryInfo.requiredConsents,
              this.entryInfo.softCreditConsentSupportedValues,
              selectedTimelineValue,
            )}
            submitForm={onSubmit}
            submitButtonDisabled={submitButtonDisabled}
            submitError={submitError}
          />
        </React.Fragment>
      );
    }

    const termsAndConditionsInputs = generateInputs(
      this.entryInfo.requiredConsents,
      this.entryInfo.softCreditConsentSupportedValues,
      selectedTimelineValue,
      phoneNumber,
    );

    return (
      <div data-it="JourneyChooser-termsAndConditionsInputs">
        <div>
          {termsAndConditionsInputs.map(input => (
            <div className={css(paneFormStyles.fieldWrapper, styles.inputBorderBottom)} key={input.name}>
              <div className={css(styles.fieldLabel)}>
                {input.label && (
                  <label htmlFor={input.name}>
                    <Heading
                      text={input.label.toUpperCase()}
                      rank={2}
                      fontSize={12}
                      font="Roboto"
                      weight={600}
                      color={deploymentConfig.getOrganizationColors().action}
                    />
                  </label>
                )}
              </div>
              <Field {..._.omit(input, 'label')} />
            </div>
          ))}
        </div>
        {!this.useRequiredViewingInput && (
          <React.Fragment>
            <div className={css(paneFormStyles.buttonDisplay, paneFormStyles.buttonPadding)}>
              <PrimaryButton
                isWide
                text="Continue"
                fontFamily="Roboto"
                data-it="JourneyChooser-continue"
                disabled={submitButtonDisabled}
              />
            </div>
            {submitError && (
              <div className={css(styles.errorWrapper)}>
                <ErrorMessage errorText={submitError} />
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    );
  };

  render() {
    const { handleSubmit, selectedTimelineValue } = this.props;
    const { isFetchingEntryInfo } = this.state;
    const onSubmit = handleSubmit!(this.onJourneyChooserSubmit);

    const loanTypeConfig = _.find(loanTypeChooserConfigs, { value: this.entryInfo.loanType }) as LoanTypeButtonConfig;

    return (
      <React.Fragment>
        {isFetchingEntryInfo ? (
          <div className={css(paneFormStyles.wrapper)}>
            <div className={css(styles.loadingSpinner)}>
              <LoadingSpinner data-it="JourneyChooser-loadingSpinner" />
            </div>
          </div>
        ) : (
          <form
            noValidate
            onSubmit={onSubmit}
            className={css(paneFormStyles.wrapper)}
            data-it="JourneyChooser"
            // Set form name here so that we can submit it using buttons outside the form
            // (i.e. from the RequiredViewingRequirementInput modal)
            id={onboardingForm.FORM_NAME}
          >
            <span id="js-form-content" className={css(styles.srOnlyContentText, a11y.srOnly)} tabIndex={-1}>
              Where are you in the process
            </span>
            {this.entryInfo.showChosenLoanType && (
              <div className={css(styles.loanTypeWrapper)}>
                <LargeIconButton
                  {...loanTypeConfig}
                  key={loanTypeConfig.value}
                  onClick={_.noop}
                  selectable={false}
                  type="button"
                  data-it="JourneyChooser-loanType"
                />
              </div>
            )}

            {this.entryInfo.showTimelineConfigs && (
              <TitleCard>
                <div className={css(styles.timelineContainer)}>
                  <TransitioningFields
                    key="timelineFields"
                    data-it="JourneyChooser-timelineFields"
                    fields={this.getTimelineTransitionConfigs()}
                  />
                </div>
              </TitleCard>
            )}

            <TransitioningFields
              data-it="JourneyChooser-submitWrapper"
              fields={[
                {
                  key: 'JourneyChooser-submitWrapper',
                  in: !(this.entryInfo.showTimelineConfigs && !selectedTimelineValue),
                  style: 'fade',
                  component: (
                    <div className={css(paneFormStyles.inputsWrapper)}>
                      <TitleCard>
                        <div className={css(styles.cardPadding)}>{this.renderTermsAndConditions(onSubmit)}</div>
                      </TitleCard>
                    </div>
                  ),
                },
              ]}
            />
          </form>
        )}
      </React.Fragment>
    );
  }
}

export default JourneyChooser;
